import { type ReactElement, useEffect, useMemo, useState } from 'react';
import { toolbarControlRadius } from '@/features/shared/components/tableSystem/tableToolbar.helpers';
import { EntityTagsField } from '@/features/shared/components/tags/EntityTagsField';
import { getFormLabel } from '@/shared/copy/forms';
import { useTagOptions } from '@/shared/hooks';
import { s } from '@/theme/tokens';
import { withStatusFilterOptionIcons } from '@/ui/composites/filters/statusFilterOptionIcons';
import { TagsFilterField } from '@/ui/composites/filters/TagsFilterField';
import { Box } from '@/ui/primitives';
import { DatePicker } from '@/ui/primitives/DatePicker';
import { defaultTagColor } from './EditableTagsSection.utils';
import { editDialogInputBackgroundColor } from './EntityEditDialog';
import type { TagColor } from '@/shared/data/tags';
import type { Dayjs } from 'dayjs';

type EntityEditOption = {
  value: string;
  label: string;
};

type EntityEditSelectFieldProps = {
  label: string;
  value: string;
  options: EntityEditOption[];
  emptyLabel?: string;
  fullWidth?: boolean;
  controlWidthPx?: number;
  onChange: (nextValue: string) => void;
};

export const EntityEditSelectField = ({
  label,
  value,
  options,
  emptyLabel = label,
  fullWidth = true,
  controlWidthPx,
  onChange,
}: EntityEditSelectFieldProps): ReactElement => (
  <TagsFilterField
    label={label}
    value={value.length > 0 ? [value] : []}
    options={options}
    multiple={false}
    showClearButton={false}
    labelPlacement="stacked"
    emptyLabel={emptyLabel}
    fullWidth={fullWidth}
    controlWidthPx={controlWidthPx}
    controlHeight={36}
    controlRadius={toolbarControlRadius}
    highlightActiveValue={false}
    inputLeftPadding={s('xs')}
    inputBackgroundColor={editDialogInputBackgroundColor}
    dropdownBackgroundColor={editDialogInputBackgroundColor}
    onChange={(nextValues) => {
      onChange(nextValues[0] ?? '');
    }}
  />
);

type EntityEditStatusFieldProps<TStatus extends string> = {
  value: TStatus;
  options: Array<{ value: TStatus; label: string }>;
  onChange: (nextValue: TStatus) => void;
};

export const EntityEditStatusField = <TStatus extends string>({
  value,
  options,
  onChange,
}: EntityEditStatusFieldProps<TStatus>): ReactElement => {
  const optionsWithIcons = useMemo(() => withStatusFilterOptionIcons(options), [options]);

  return (
    <EntityEditSelectField
      label={getFormLabel('status')}
      value={value}
      options={optionsWithIcons}
      fullWidth={false}
      controlWidthPx={180}
      onChange={(nextValue) => {
        onChange(nextValue as TStatus);
      }}
    />
  );
};

type EntityEditDateFieldProps = {
  label: string;
  value: Dayjs | null;
  onChange: (nextValue: Dayjs | null) => void;
};

export const EntityEditDateField = ({
  label,
  value,
  onChange,
}: EntityEditDateFieldProps): ReactElement => (
  <Box sx={{ width: '200px', maxWidth: '100%' }}>
    <DatePicker
      label={label}
      value={value}
      fieldStyle="settings"
      inputBackgroundColor={editDialogInputBackgroundColor}
      onChange={onChange}
    />
  </Box>
);

type EntityEditTagsFieldProps = {
  open: boolean;
  projectId?: string | null;
  tags: string[];
  helperText?: string;
  onTagsChange: (tags: string[]) => void;
};

export const EntityEditTagsField = ({
  open,
  projectId,
  tags,
  helperText,
  onTagsChange,
}: EntityEditTagsFieldProps): ReactElement => {
  const { tagOptions } = useTagOptions({ projectId, enabled: open });
  const initialTagColors = useMemo(
    () =>
      tags.map((tag) => {
        const matchingOption = tagOptions.find((option) => option.value === tag);
        return matchingOption?.color ?? defaultTagColor;
      }),
    [tagOptions, tags],
  );
  const [colors, setColors] = useState<TagColor[]>(initialTagColors);

  useEffect(() => {
    if (!open) {
      return;
    }
    setColors(initialTagColors);
  }, [initialTagColors, open]);

  return (
    <EntityTagsField
      tags={tags}
      colors={colors}
      helperText={helperText}
      tagOptions={tagOptions}
      onTagsChange={onTagsChange}
      onColorsChange={setColors}
    />
  );
};
